import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import {
  getProjectProcesses,
  ProjectStage,
  type ProjectProcess,
} from 'api/projectProcess';
import { RootState, ThunkApi } from 'state/store';
import { SLICE_STATUS } from 'utils/constants';
import { Status } from 'utils/customTypes';
import { upsertStages } from './stagesSlice';

interface ProcessWithoutStages extends Omit<ProjectProcess, 'projectStages'> {}

const processesAdapter = createEntityAdapter<ProcessWithoutStages>();

interface ProcessesState {
  status?: Status;
  selectedProcessIds?: string[];
}

const initialState: ProcessesState = {};

/* ============================== REDUX THUNK =============================== */

const concurrencyCondition = (
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean => {
  const { status } = getProcessesState(getState());
  return status !== SLICE_STATUS.LOADING;
};

export const getProcessesList = createAsyncThunk<
  ProcessWithoutStages[],
  void,
  ThunkApi
>(
  'projectsList/filters/processes/getProcessesList',
  async (_, { dispatch }) => {
    const response = await getProjectProcesses();
    const processes = response.data;
    const processesWithoutStages: ProcessWithoutStages[] = [];
    const stages: ProjectStage[] = [];
    for (const process of processes) {
      const { projectStages, ...rest } = process;
      processesWithoutStages.push(rest);
      stages.push(...projectStages);
    }
    dispatch(upsertStages(stages));
    return processesWithoutStages;
  },
  { condition: concurrencyCondition }
);

const processesSlice = createSlice({
  name: 'projectsList/filters/processes',
  initialState: processesAdapter.getInitialState(initialState),
  reducers: {
    setSelectedProcessIds: (state, action) => {
      state.selectedProcessIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProcessesList.fulfilled, (state, action) => {
        processesAdapter.upsertMany(state, action.payload);
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(getProcessesList.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getProcessesList.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      });
  },
});

const getProcessesState = (state: RootState) =>
  state.projectsList.filters.processes;

const toProcess = (process: ProcessWithoutStages) => {
  return {
    id: process.id,
    name: process.processName,
  };
};

export const { selectAll: selectAllProcesses, selectById: selectProcessById } =
  processesAdapter.getSelectors((state: RootState) => getProcessesState(state));

export const selectProcesses = createSelector(
  [selectAllProcesses],
  (processes) => {
    if (!processes || processes.length === 0) return [];
    return processes.map(toProcess);
  }
);

export const selectSelectedProcessIds = (state: RootState) =>
  getProcessesState(state).selectedProcessIds || [];

export const selectSelectedProcesses = createSelector(
  [selectAllProcesses, selectSelectedProcessIds],
  (processes, selectedProcessIds) => {
    return processes
      .filter((process) => selectedProcessIds.includes(process.id))
      .map(toProcess);
  }
);

export const { setSelectedProcessIds } = processesSlice.actions;

export default processesSlice.reducer;
